import React, { useState, useEffect } from 'react';
import Module from '../../../../layout/Module';
import DataGrid from '../../../../table/DataGrid';
import TemplateDropZonePreviewList from './TemplateDropZonePreviewList';
import FileDropZone from '../../../../commom/FileDropZone';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { handlerErrorV2 } from '../../../../../utils/errors';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { importTemplates, areTemplateNamesTaken } from '../../../../../services/data-entry/templates.service';
import { useSnackbar } from 'notistack';

const maxFileSize = 100; //100MB for now
const fileSizeUnit = 'MB';

//Read about supported MIME types https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
const acceptedFileTypes = {
  'application/json': ['.json']
};

const TemplateDropZone = ({ templateType, templateTypes = [], handleClose, handleRefresh }) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [templatesFromJson, setTemplatesFromJson] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const permissions =
    templateType?.value === 'assessment'
      ? useSelector((state) => state?.session?.permissions?.templateassessmentgenerator ?? {})
      : useSelector((state) => state?.session?.permissions?.templatesurveygenerator ?? {});
  const columnsTable = useSelector((state) => state?.datatable?.importTemplates?.columnsTable ?? []);
  const skip = useSelector((state) => state?.datatable?.importTemplates?.skip ?? 0);
  const rowsPerPage = useSelector((state) => state?.datatable?.importTemplates?.rowsPerPage ?? 100);
  const totalRecords = templatesFromJson?.length || 0;
  const totalNotTaken = templatesFromJson?.filter((template) => !template?.isTaken)?.length || 0;

  const filterTemplates = (templates = [], skipValue = 0, rowsPerPageValue = 50) => {
    if (
      !Array.isArray(templates) ||
      !Number.isInteger(skipValue) ||
      !Number.isInteger(rowsPerPageValue) ||
      skipValue < 0 ||
      rowsPerPageValue <= 0
    ) {
      return templates;
    }

    const startIndex = skipValue * rowsPerPageValue;
    const endIndex = startIndex + rowsPerPageValue;
    const slicedTemplates = templates?.slice(startIndex, Math.min(endIndex, templates.length));

    return slicedTemplates;
  };

  const list = filterTemplates(templatesFromJson, skip, rowsPerPage);

  const onDeleteFile = async (fileToDelete) => {
    const updatedFiles = files.filter((file) => file !== fileToDelete);
    setFiles(updatedFiles);

    if (updatedFiles.length === 0) {
      setTemplatesFromJson([]);
      setCheckedIds([]);
      setError(null);
      return;
    }

    const deletedFileIndex = files?.findIndex((file) => file === fileToDelete);

    const updatedTemplates = templatesFromJson?.filter(
      (template) => template?.fileIndex !== deletedFileIndex
    );

    const templatesFromDeletedFile = templatesFromJson?.filter(
      (template) => template?.fileIndex === deletedFileIndex
    );
    const idsToRemove = templatesFromDeletedFile?.map((template) => template?._id);
    const updatedCheckedIds = checkedIds?.filter((id) => !idsToRemove?.includes(id));

    setTemplatesFromJson(updatedTemplates);
    setCheckedIds(updatedCheckedIds);

    // Clear error if it was related to the deleted file
    if (error && error.includes(fileToDelete.file.name)) {
      setError(null);
    }
  };

  const onSaveTemplates = async () => {
    try {
      setIsSaving(true);

      if (!templateType) {
        setError('Template type is not available for this school. Please contact your administrator.');
        return;
      }

      const filteredTemplates = templatesFromJson
        ?.filter((template) => checkedIds?.some((id) => template?._id === id))
        ?.map((template) => {
          return {
            title: template?.title,
            yearLevels: [],
            roles: [],
            questions: template?.questions,
            type: templateType
          };
        });

      if (filteredTemplates?.length <= 0) {
        setError('Please select at least one template');
        return;
      }

      const { data, error } = await importTemplates(filteredTemplates, templateType?.value ?? '');

      if (error || !data) {
        enqueueSnackbar('We could not import the templates, please try again', {
          variant: 'error'
        });
      } else {
        enqueueSnackbar('Templates imported successfully', { variant: 'success' });
        handleRefresh();
        return;
      }
    } catch (err) {
      const message = handlerErrorV2('Could not import the templates. Please try again.');
      setError(message);
      setIsSaving(true);
    }
  };

  const onChecked = (ids) => setCheckedIds(ids);

  const onCheckOne = (id, checked) => {
    if (checked) {
      onChecked([...checkedIds, id]);
    } else {
      onChecked(checkedIds?.filter((item) => item !== id));
    }
  };

  const onCheckedAll = (checked) => {
    let idsToCheck = [];

    if (checked && checkedIds?.length !== totalNotTaken) {
      idsToCheck = templatesFromJson.filter((item) => !item?.isTaken)?.map((item) => String(item._id));
    }

    onChecked(checked ? idsToCheck : []);
  };

  useEffect(() => {
    if (files?.length > 0) {
      setIsLoading(true);
      setError(null);
      setTemplatesFromJson([]);
      setCheckedIds([]);

      const processFiles = async () => {
        try {
          // Create FileReader promises for all files
          const filePromises = files.map((fileObj, fileIndex) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();

              reader.onload = async (event) => {
                try {
                  const fileContent = event.target.result;
                  const parsedJson = JSON.parse(fileContent) || {};
                  const templates = parsedJson?.templates || [];
                  const filteredTemplates = templates.filter(
                    (el) => el?.templateType?.value === templateType?.value
                  );

                  // Add file tracking info
                  filteredTemplates.forEach((template, templateIndex) => {
                    template.sourceFile = fileObj.file.name;
                    template.fileIndex = fileIndex;
                    // Ensure unique IDs across all files
                    template._id = template._id
                      ? `${template._id}_file_${fileIndex}`
                      : `template_${fileIndex}_${templateIndex}`;
                  });

                  resolve({
                    success: true,
                    templates: filteredTemplates,
                    fileName: fileObj.file.name
                  });
                } catch (parseError) {
                  resolve({
                    success: false,
                    error: `${fileObj.file.name}: Invalid JSON format - ${parseError.message}`,
                    fileName: fileObj.file.name
                  });
                }
              };

              reader.onerror = () => {
                resolve({
                  success: false,
                  error: `${fileObj.file.name}: Failed to read file`,
                  fileName: fileObj.file.name
                });
              };

              reader.readAsText(fileObj.file);
            });
          });

          // Process all files concurrently
          const results = await Promise.allSettled(filePromises);

          // Collect templates and errors
          const allTemplates = [];
          const errors = [];

          results.forEach((result) => {
            if (result.status === 'fulfilled') {
              const { success, templates, error, fileName } = result.value;
              if (success) {
                allTemplates.push(...templates);
              } else {
                errors.push(error);
              }
            } else {
              errors.push(`Unexpected error processing file: ${result.reason}`);
            }
          });

          // Handle errors
          if (errors.length > 0) {
            setError(`Some files had errors:\n${errors.join('\n')}`);
          }

          // Handle templates
          if (allTemplates.length <= 0) {
            setError(
              errors.length > 0
                ? 'No valid templates found in any files due to errors above.'
                : 'No templates found matching the selected template type in any of the uploaded files.'
            );
          } else {
            // Check for duplicate names
            const typeId = templateTypes?.find((type) => type?.value === templateType?.value)?._id;
            const templateNames = allTemplates.map((template) => template?.title);
            const { data } = await areTemplateNamesTaken(typeId, templateNames);

            if (data?.length > 0) {
              allTemplates.forEach((template) => {
                template.isTaken = data?.some((el) => el?.title === template?.title);
              });
            }

            setTemplatesFromJson(allTemplates);

            // Auto-check templates that are not taken and available for import
            const availableTemplateIds = allTemplates
              .filter((template) => !template.isTaken)
              .map((template) => String(template._id));

            setCheckedIds(availableTemplateIds);
          }
        } catch (error) {
          setError('Unexpected error processing files: ' + error.message);
        } finally {
          setIsLoading(false);
        }
      };

      processFiles();
    } else {
      // Reset state when no files
      setTemplatesFromJson([]);
      setCheckedIds([]);
      setError(null);
    }
  }, [files, templateType?.value, templateTypes]);

  return (
    <>
      <Module.BodyContainer>
        <FileDropZone
          files={files}
          setFiles={setFiles}
          isLoading={isLoading}
          acceptedFileTypes={acceptedFileTypes}
          maxFileSize={maxFileSize}
          fileSizeUnit={fileSizeUnit}
          supportedFormatsDescription={'.json'}
          maxFiles={0}
          title='Import templates'
          className='h-56'
          usePreviousState={false}
        />

        <TemplateDropZonePreviewList
          files={files}
          setFiles={setFiles}
          isLoading={isLoading}
          uploaded={[]}
          onDeleteFile={onDeleteFile}
          percent={0}
          error={error}
          currentUpload={[]}
          isFileValid={files?.length > 0 ? templatesFromJson?.length > 0 : true}
        />
        <Box
          sx={{
            minHeight: '100%',
            height: 'auto',
            width: '100%',
            position: 'relative',
            marginBottom: '8rem'
          }}
        >
          <DataGrid
            className='pb-24'
            id='table-assessment-templates'
            rows={list?.map((row, index) => {
              const isTaken = row?.isTaken ?? false;
              return {
                ...row,
                index: index + 1,
                name: (
                  <span className={`${isTaken ? 'template-item--disabled' : ''}`}>{row.title || '-'}</span>
                ),
                questions: (
                  <span className={`${isTaken ? 'template-item--disabled' : ''}`}>
                    {row?.questions?.filter((question) => question?.type?.value != 'groupreference').length ||
                      0}
                  </span>
                ),
                type: (
                  <span className={`${isTaken ? 'template-item--disabled' : ''}`}>
                    {row?.templateType?.title || '-'}
                  </span>
                ),
                checkIds: (
                  <>
                    {permissions?.export && (
                      <Checkbox
                        id={`checkbox-id-template-${row._id}`}
                        checked={!isTaken && checkedIds.includes(row._id)}
                        onChange={isTaken ? () => {} : (e) => onCheckOne(row._id, e.target.checked)}
                        color='primary'
                        disabled={isLoading || isTaken}
                        className={`${isTaken ? 'disabled ' : ''}`}
                      />
                    )}
                  </>
                ),
                message: isTaken && (
                  <div className='flex items-center gap-1'>
                    <span className='text-12 font-semibold text-template-error '>
                      Template name already exists
                    </span>
                  </div>
                )
              };
            })}
            loading={isLoading}
            labelNoRows='No templates found'
            nameModule='importTemplates'
            headers={[...columnsTable]}
            totalRows={totalRecords || 0}
            skip={skip}
            rowsPerPage={rowsPerPage}
            columns={[
              {
                label: '#',
                key: 'index',
                align: 'center',
                width: 10,
                show: true,
                hide: false
              },
              {
                show: permissions['export'],
                key: 'checkIds',
                align: 'center',
                width: 15,
                label: (
                  <Checkbox
                    id={`checkbox-id-user-header`}
                    checked={checkedIds.length > 0 && checkedIds.length === totalNotTaken}
                    indeterminate={checkedIds.length > 0 && checkedIds.length < totalNotTaken}
                    onChange={(e) => onCheckedAll(e.target.checked)}
                    color='primary'
                    disabled={isLoading || totalNotTaken === 0}
                  />
                )
              },
              ...columnsTable
            ]}
            orderByField='name'
            actionsSetting={
              checkedIds.length > 0 && (
                <Typography className='min-w-100px' align='right'>
                  Rows selected {checkedIds.length}
                </Typography>
              )
            }
          />
        </Box>
      </Module.BodyContainer>
      <Box
        className='absolute bottom-0 inset-x-0 flex justify-end gap-4 py-4 px-8'
        sx={{ zIndex: 10, backgroundColor: '#fff' }}
      >
        <Button
          size='large'
          color='primary'
          onClick={handleClose}
          variant='outlined'
          className='shadow-sm'
          startIcon={<FontAwesomeIcon icon='times' />}
          style={{ padding: '6px 16px', height: '40px' }}
          disabled={isSaving}
        >
          Close
        </Button>
        {permissions?.export && (
          <Box sx={{ display: 'flex', position: 'relative' }}>
            {isSaving && (
              <CircularProgress
                size={25}
                style={{ position: 'absolute', left: '35%', top: '8px', zIndex: 1 }}
              />
            )}
            <Button
              size='large'
              color='primary'
              onClick={onSaveTemplates}
              variant='contained'
              className='shadow-sm'
              startIcon={<FontAwesomeIcon icon='check' />}
              style={{ padding: '6px 16px', height: '40px' }}
              disabled={checkedIds.length === 0 || isLoading}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default TemplateDropZone;
